import { useState } from "react";
import { useAppFeedback } from "../../useAppFeedback";
import dayjs, { Dayjs } from "dayjs";
import { AuthUser } from "../../../context/AuthContext";
import { DrinkOption } from "../../../services/drinkService";
import { outbox } from "../../../offline";
import { DEFAULT_DRINK_DURATION_MINUTES } from "../drinkStats/constants";
import { useTranslation } from "react-i18next";

type SubmitOptions = {
    user: AuthUser | null;
    t: ReturnType<typeof useTranslation>["t"];
    drinks: DrinkOption[];
    selectedDrinkId: number | null;
    setSelectedDrinkId: (value: number | null) => void;
    amountMl: number;
    setAmountMl: (value: number) => void;
    consumedAt: Dayjs | null;
    durationMinutes: number;
    startAtOverride: Dayjs | null;
    derivedStart: Dayjs | null;
    isOnline: boolean;
    setLastAddedDrink: (value: { name: string; amount: number } | null) => void;
    setSelectedPreset: (value: "custom" | "shot" | "double" | "longdrink" | "beer" | "mass") => void;
    setConsumedAt: (value: Dayjs | null) => void;
    setDurationMinutes: (value: number) => void;
    setSelectedDurationPreset: (value: number | "custom") => void;
    setStartAtOverride: (value: Dayjs | null) => void;
    triggerPostAddCleanup: () => void;
};

export const useAddDrinkSubmit = ({
    user, t, drinks, selectedDrinkId, setSelectedDrinkId, amountMl, setAmountMl, consumedAt, durationMinutes, startAtOverride,
    derivedStart, isOnline, setLastAddedDrink, setSelectedPreset, setConsumedAt, setDurationMinutes,
    setSelectedDurationPreset, setStartAtOverride, triggerPostAddCleanup,
}: SubmitOptions) => {
    const { message } = useAppFeedback();
    const [submitting, setSubmitting] = useState(false);
    const handleSubmit = async (recordNowChoice = false) => {
        const recordNow = recordNowChoice === true;
        if (!user) {
            message.info(t("addDrink.messages.loginToAdd"));
            return;
        }

        if (selectedDrinkId === null) {
            message.warning(t("addDrink.messages.selectDrink"));
            return;
        }

        const drink = drinks.find((item) => item.id === selectedDrinkId);
        if (!drink) {
            message.error(t("addDrink.messages.drinkNotFound"));
            return;
        }

        if (!amountMl || amountMl <= 0) {
            message.warning(t("addDrink.messages.invalidAmount"));
            return;
        }

        const consumedDate = recordNow || !consumedAt ? new Date() : consumedAt.toDate();
        const fallbackStart = consumedAt && derivedStart ? derivedStart.toDate() : consumedDate;
        const startedAtDate = recordNow
            ? startAtOverride?.toDate() ?? new Date(consumedDate.getTime() - durationMinutes * 60 * 1000)
            : fallbackStart;

        setSubmitting(true);
        try {
            // Always queue first, then flush opportunistically - never call the network directly
            // here. This is what makes logging a drink work with no connection at all: the write
            // is confirmed to the user the instant it's saved locally, not after a round trip
            // that may never complete. The outbox sends each entry with an idempotency key, so a retry cannot log it twice;
            // useOutboxSync (App.tsx) and the flush() call below decide when it actually
            // gets sent.
            await outbox.enqueue({
                drink: { drinkid: drink.id, drinkname: drink.name, promille: drink.promille },
                amount: amountMl,
                tsInsert: consumedDate.getTime(),
                tsStart: startedAtDate.getTime(),
                user: { userid: user.userId, username: user.username },
            });
            message.success(
                isOnline
                    ? t("addDrink.notifications.drinkRecorded")
                    : t("addDrink.notifications.drinkQueued")
            );
            setLastAddedDrink({ name: drink.name, amount: amountMl });
            setSelectedDrinkId(null);
            setAmountMl(500);
            setSelectedPreset("custom");
            setConsumedAt(dayjs());
            setDurationMinutes(DEFAULT_DRINK_DURATION_MINUTES);
            setSelectedDurationPreset(DEFAULT_DRINK_DURATION_MINUTES);
            setStartAtOverride(null);
            triggerPostAddCleanup();
            // Fire-and-forget: the user already has confirmation the drink is saved, so this
            // shouldn't make them wait on a network round trip. If it fails (still offline), the
            // entry just stays queued for useOutboxSync's next automatic trigger.
            void outbox.flush();
        } catch {
            message.error(t("addDrink.errors.addDrink"));
        } finally {
            setSubmitting(false);
        }
    };
    return { handleSubmit, submitting };
};
